import InvoicePreview from '../components/InvoicePreview';
import React, { useState, useEffect } from 'react';
import { invoiceApi, bookingApi, customerApi, depositApi, receiptApi, creditNoteApi, paymentApi, settingsApi, formatApiError } from '../services/api';
import { 
  Receipt, Funnel, Eye, FileArrowDown, Plus,
  CheckCircle, Clock, Warning, ShieldCheck, ArrowUDownLeft, Trash, HandCoins, Bell, X, PencilSimple, ClockCounterClockwise, ReceiptX, CreditCard, Envelope
} from '@phosphor-icons/react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import SignaturePad from '../components/SignaturePad';
import QuickCustomerCreate from '../components/QuickCustomerCreate';
import RevisionHistory from '../components/RevisionHistory';
import CustomerOverrideFields from '../components/CustomerOverrideFields';

const INV_STATUS = { pending: 'Ausstehend', partial: 'Teilbezahlt', paid: 'Bezahlt', overdue: 'Überfällig', cancelled: 'Storniert', open: 'Offen', issued: 'Ausgestellt' };
const DEP_STATUS = { hinterlegt: 'Hinterlegt', 'zurückgegeben': 'Zurückgegeben' };
const REMINDER_LABELS = { 0: '—', 1: 'Erinnerung', 2: '1. Mahnung', 3: '2. Mahnung', 4: 'Letzte Mahnung' };

const Invoices = () => {
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [onlinePaymentsEnabled, setOnlinePaymentsEnabled] = useState(false);
  useEffect(() => {
    let active = true;
    settingsApi.get().then(({ data }) => {
      if (active) setOnlinePaymentsEnabled(data?.online_payments_enabled === true);
    }).catch(() => { if (active) setOnlinePaymentsEnabled(false); });
    return () => { active = false; };
  }, []);
  const [invoices, setInvoices] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('invoices');

  // Modals
  const [showBookingInvoice, setShowBookingInvoice] = useState(false);
  const [showFreeInvoice, setShowFreeInvoice] = useState(false);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showReturnDeposit, setShowReturnDeposit] = useState(false);
  const [returningDeposit, setReturningDeposit] = useState(null);
  const [returnSignature, setReturnSignature] = useState(null);
  const [returnMethod, setReturnMethod] = useState('bar');

  // Booking invoice
  const [bookingsWithoutInvoice, setBookingsWithoutInvoice] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [additionalCharges, setAdditionalCharges] = useState(0);
  const [invoiceDiscount, setInvoiceDiscount] = useState(0);

  // Free invoice
  const [freeCustomerId, setFreeCustomerId] = useState('');
  const [lineItems, setLineItems] = useState([{ description: '', quantity: 1, unit_price: 0 }]);
  const [freeDiscount, setFreeDiscount] = useState(0);
  const [freeNotes, setFreeNotes] = useState('');

  // Edit invoice
  const [showEditInvoice, setShowEditInvoice] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editInvItems, setEditInvItems] = useState([]);
  const [editInvDiscount, setEditInvDiscount] = useState(0);
  const [editInvAdditional, setEditInvAdditional] = useState(0);
  const [editInvNotes, setEditInvNotes] = useState('');
  const [editInvCustomerId, setEditInvCustomerId] = useState('');
  const [editInvDueDate, setEditInvDueDate] = useState('');
  const [editInvTaxRate, setEditInvTaxRate] = useState(19);
  const [editInvCustomerOverride, setEditInvCustomerOverride] = useState(null);

  // Deposit
  const [depCustomerId, setDepCustomerId] = useState('');
  const [depAmount, setDepAmount] = useState('');
  const [depDescription, setDepDescription] = useState('Kaution');
  const [depPaymentMethod, setDepPaymentMethod] = useState('bar');
  const [depNotes, setDepNotes] = useState('');

  // Receipt
  const [rcptCustomerId, setRcptCustomerId] = useState('');
  const [rcptAmount, setRcptAmount] = useState('');
  const [rcptDescription, setRcptDescription] = useState('');
  const [rcptMethod, setRcptMethod] = useState('bar');
  const [rcptSignature, setRcptSignature] = useState(null);
  const [rcptNotes, setRcptNotes] = useState('');
  const [showQuickCustomer, setShowQuickCustomer] = useState(false);
  const [historyInvoice, setHistoryInvoice] = useState(null);
  // Gutschrift (credit note) states
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditInvoice, setCreditInvoice] = useState(null); // invoice for Variante 1 (null = Variante 2 standalone)
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditNotes, setCreditNotes] = useState('');
  const [creditApplyToPaid, setCreditApplyToPaid] = useState(false);
  const [creditCustomerId, setCreditCustomerId] = useState('');
  const [creditDescription, setCreditDescription] = useState('');
  const [creditType, setCreditType] = useState('standard');
  const [creditCustomLegal, setCreditCustomLegal] = useState('');
  const [editingCreditNote, setEditingCreditNote] = useState(null);
  const [creditCustomerOverride, setCreditCustomerOverride] = useState(null);

  useEffect(() => { loadAll(); }, [filterStatus, activeTab]);

  const loadAll = async () => {
    try {
      const [invRes, custRes] = await Promise.all([
        invoiceApi.getAll(filterStatus !== 'all' ? { status: filterStatus } : {}),
        customerApi.getAll()
      ]);
      setInvoices(invRes.data);
      setCustomers(custRes.data);
      
      if (activeTab === 'deposits') {
        const depRes = await depositApi.getAll({});
        setDeposits(depRes.data);
      }
      if (activeTab === 'receipts') {
        const rcptRes = await receiptApi.getAll();
        setReceipts(rcptRes.data);
      }
      if (activeTab === 'dunning') {
        const odRes = await invoiceApi.getOverdue();
        setOverdueInvoices(odRes.data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // ---- Booking Invoice ----
  const openBookingInvoice = async () => {
    try {
      const [bRes, iRes] = await Promise.all([bookingApi.getAll(), invoiceApi.getAll()]);
      const invoiced = new Set(iRes.data.map(i => i.booking_id));
      setBookingsWithoutInvoice(bRes.data.filter(b => !invoiced.has(b.id)));
      setSelectedBookingId(''); setAdditionalCharges(0); setInvoiceDiscount(0);
      setShowBookingInvoice(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitBookingInvoice = async () => {
    if (!selectedBookingId) return;
    try {
      await invoiceApi.create({ booking_id: selectedBookingId, additional_charges: additionalCharges, discount: invoiceDiscount });
      toast.success('Rechnung erstellt'); setShowBookingInvoice(false); loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- Free Invoice ----
  const addLineItem = () => setLineItems([...lineItems, { description: '', quantity: 1, unit_price: 0 }]);
  const removeLineItem = (i) => setLineItems(lineItems.filter((_, idx) => idx !== i));
  const updateLineItem = (i, field, value) => {
    const updated = [...lineItems];
    updated[i] = { ...updated[i], [field]: value };
    setLineItems(updated);
  };
  const freeTotal = lineItems.reduce((s, it) => s + (it.quantity * it.unit_price), 0) - freeDiscount;

  const submitFreeInvoice = async () => {
    if (!freeCustomerId || lineItems.length === 0) return;
    const validItems = lineItems.filter(it => it.description && it.unit_price > 0);
    if (validItems.length === 0) { toast.error('Mindestens eine Position erforderlich'); return; }
    try {
      await invoiceApi.createFree({ customer_id: freeCustomerId, line_items: validItems, discount: freeDiscount, notes: freeNotes || null });
      toast.success('Freie Rechnung erstellt'); setShowFreeInvoice(false); loadAll();
      setLineItems([{ description: '', quantity: 1, unit_price: 0 }]); setFreeDiscount(0); setFreeNotes('');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- Edit Invoice ----
  const openEditInvoice = (inv) => {
    if (inv.invoice_type === 'storno') {
      toast.error('Stornorechnungen können nicht bearbeitet werden');
      return;
    }
    setEditingInvoice(inv);
    setEditInvItems(
      (inv.line_items || []).length > 0
        ? inv.line_items.map(li => ({
            description: li.description || '',
            quantity: li.quantity || 1,
            unit_price: li.unit_price || 0,
          }))
        : [{ description: '', quantity: 1, unit_price: 0 }]
    );
    setEditInvDiscount(inv.discount || 0);
    setEditInvAdditional(inv.additional_charges || 0);
    setEditInvNotes(inv.notes || '');
    setEditInvCustomerId(inv.customer_id || '');
    setEditInvDueDate(inv.due_date || '');
    setEditInvTaxRate(inv.tax_rate || 19);
    setEditInvCustomerOverride(inv.customer_override || null);
    setShowEditInvoice(true);
  };

  const editInvAddItem = () => setEditInvItems([...editInvItems, { description: '', quantity: 1, unit_price: 0 }]);
  const editInvRemoveItem = (i) => setEditInvItems(editInvItems.filter((_, idx) => idx !== i));
  const editInvUpdateItem = (i, field, val) => {
    const updated = [...editInvItems];
    updated[i] = { ...updated[i], [field]: field === 'description' ? val : parseFloat(val) || 0 };
    setEditInvItems(updated);
  };
  const editInvTotal = editInvItems.reduce((s, it) => s + (it.quantity * it.unit_price), 0) + Number(editInvAdditional || 0) - Number(editInvDiscount || 0);

  // ---- Gutschrift (Credit Note) ----
  const openCreditModalForInvoice = (inv) => {
    setEditingCreditNote(null);
    setCreditInvoice(inv);
    setCreditAmount('');
    setCreditReason('');
    setCreditNotes('');
    setCreditApplyToPaid(false);
    setCreditCustomerId('');
    setCreditDescription('');
    setCreditType('standard');
    setCreditCustomLegal('');
    setCreditCustomerOverride(null);
    setShowCreditModal(true);
  };

  const openStandaloneCreditModal = () => {
    setEditingCreditNote(null);
    setCreditInvoice(null);
    setCreditAmount('');
    setCreditReason('');
    setCreditNotes('');
    setCreditCustomerId(customers[0]?.id || '');
    setCreditDescription('');
    setCreditType('standard');
    setCreditCustomLegal('');
    setCreditCustomerOverride(null);
    setShowCreditModal(true);
  };

  const openEditCreditNote = (cn) => {
    setEditingCreditNote(cn);
    setCreditInvoice(null);
    const firstItem = (cn.line_items && cn.line_items[0]) || {};
    const totalGross = Number(cn.total || 0);
    setCreditAmount(totalGross > 0 ? totalGross.toFixed(2) : '');
    setCreditReason(cn.reason || '');
    setCreditNotes(cn.notes || '');
    setCreditCustomerId(cn.customer_id || '');
    setCreditDescription(firstItem.description || '');
    const ct = cn.credit_type || 'standard';
    setCreditType(ct);
    setCreditCustomLegal(ct === 'custom' ? (cn.legal_note || '') : '');
    setCreditApplyToPaid(false);
    setCreditCustomerOverride(cn.customer_override || null);
    setShowCreditModal(true);
  };

  const handleDeleteCreditNote = async (cn) => {
    if (!window.confirm(`Gutschrift ${cn.invoice_number} wirklich unwiderruflich löschen?`)) return;
    try {
      await creditNoteApi.delete(cn.id);
      toast.success(`Gutschrift ${cn.invoice_number} gelöscht`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitCreditNote = async () => {
    if (!creditReason.trim()) { toast.error('Grund ist erforderlich'); return; }
    try {
      let cn;
      if (editingCreditNote) {
        // Edit mode: update existing Gutschrift
        if (!creditCustomerId) { toast.error('Kunde auswählen'); return; }
        if (!creditAmount || Number(creditAmount) <= 0) { toast.error('Betrag erforderlich'); return; }
        const res = await creditNoteApi.update(editingCreditNote.id, {
          customer_id: creditCustomerId,
          line_items: [{ description: creditDescription || creditReason, quantity: 1, unit_price: Number(creditAmount) }],
          reason: creditReason,
          notes: creditNotes || null,
          credit_type: creditType,
          legal_note: creditType === 'custom' ? creditCustomLegal : null,
          customer_override: creditCustomerOverride || {},
        });
        cn = res.data;
        toast.success(`Gutschrift ${cn.invoice_number} aktualisiert`);
        setShowCreditModal(false);
        setEditingCreditNote(null);
        loadAll();
        return;
      }
      if (creditInvoice) {
        // Variante 1: tied to invoice
        if (!creditAmount || Number(creditAmount) <= 0) { toast.error('Betrag erforderlich'); return; }
        const res = await invoiceApi.createCreditNote(creditInvoice.id, {
          amount: Number(creditAmount),
          reason: creditReason,
          notes: creditNotes || null,
          apply_to_paid_amount: creditApplyToPaid,
          credit_type: creditType,
          legal_note: creditType === 'custom' ? creditCustomLegal : null,
        });
        cn = res.data;
      } else {
        // Variante 2: standalone
        if (!creditCustomerId) { toast.error('Kunde auswählen'); return; }
        if (!creditAmount || Number(creditAmount) <= 0) { toast.error('Betrag erforderlich'); return; }
        const res = await creditNoteApi.create({
          customer_id: creditCustomerId,
          line_items: [{ description: creditDescription || creditReason, quantity: 1, unit_price: Number(creditAmount) }],
          reason: creditReason,
          notes: creditNotes || null,
          credit_type: creditType,
          legal_note: creditType === 'custom' ? creditCustomLegal : null,
        });
        cn = res.data;
      }
      toast.success(`Gutschrift ${cn.invoice_number} erstellt`);
      setShowCreditModal(false);
      loadAll();
      // Auto-download PDF
      try {
        const pdfRes = await creditNoteApi.getPdf(cn.id);
        const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gutschrift_${cn.invoice_number}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch { /* ignore */ }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadCreditNotePdf = async (inv) => {
    try {
      const res = await creditNoteApi.getPdf(inv.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gutschrift_${inv.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitEditInvoice = async () => {
    if (!editingInvoice) return;
    const validItems = editInvItems.filter(it => it.description && it.unit_price >= 0);
    if (validItems.length === 0) { toast.error('Mindestens eine Position erforderlich'); return; }
    try {
      await invoiceApi.update(editingInvoice.id, {
        customer_id: editInvCustomerId,
        line_items: validItems,
        discount: Number(editInvDiscount) || 0,
        additional_charges: Number(editInvAdditional) || 0,
        tax_rate: Number(editInvTaxRate) || 19,
        notes: editInvNotes || null,
        due_date: editInvDueDate || null,
        customer_override: editInvCustomerOverride || {},
      });
      toast.success('Rechnung aktualisiert');
      setShowEditInvoice(false);
      setEditingInvoice(null);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- Deposit ----
  const submitDeposit = async () => {
    if (!depCustomerId || !depAmount) return;
    try {
      await depositApi.create({ customer_id: depCustomerId, amount: parseFloat(depAmount), description: depDescription, payment_method: depPaymentMethod, notes: depNotes || null });
      toast.success('Kaution hinterlegt'); setShowDeposit(false); loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleReturnDeposit = async (dep) => {
    setReturningDeposit(dep);
    setReturnSignature(null);
    setReturnMethod('bar');
    setShowReturnDeposit(true);
  };

  const confirmReturnDeposit = async () => {
    if (!returningDeposit) return;
    try {
      // 1. Mark deposit as returned (with return payment method → cashbook)
      await depositApi.return(returningDeposit.id, returnMethod);
      
      // 2. Create receipt automatically
      await receiptApi.create({
        customer_id: returningDeposit.customer_id,
        deposit_id: returningDeposit.id,
        amount: returningDeposit.amount,
        description: `Kautionsrückgabe: ${returningDeposit.description}`,
        payment_method: returnMethod,
        signature_data: returnSignature,
        notes: `Kaution zurückgegeben am ${new Date().toLocaleDateString('de-DE')}`
      });
      
      toast.success('Kaution zurückgegeben & Quittung erstellt');
      setShowReturnDeposit(false);
      setReturningDeposit(null);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- Receipt ----
  const submitReceipt = async () => {
    if (!rcptCustomerId || !rcptAmount || !rcptDescription) return;
    try {
      await receiptApi.create({
        customer_id: rcptCustomerId, amount: parseFloat(rcptAmount), description: rcptDescription,
        payment_method: rcptMethod, signature_data: rcptSignature, notes: rcptNotes || null
      });
      toast.success('Quittung erstellt'); setShowReceipt(false); loadAll();
      setRcptSignature(null); setRcptDescription(''); setRcptAmount(''); setRcptNotes('');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadReceiptPdf = async (id, number) => {
    try {
      const res = await receiptApi.getPdf(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `quittung_${number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadInvoicePdf = async (inv) => {
    try {
      const res = await invoiceApi.getPdf(inv.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `rechnung_${inv.invoice_number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleStripePayment = async (inv) => {
    try {
      const res = await paymentApi.checkoutInvoice(inv.id);
      if (res.data?.url) {
        window.open(res.data.url, '_blank');
        toast.success('Stripe-Bezahlseite geöffnet');
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const getInvBadge = (s) => {
    const st = { pending: 'badge-warning', partial: 'badge-warning', paid: 'badge-success', overdue: 'badge-error', cancelled: 'badge-neutral', open: 'badge-warning', issued: 'badge-success' };
    return <span className={`badge ${st[s] || 'badge-neutral'} flex items-center`}>{INV_STATUS[s] || s}</span>;
  };

  const handleCancelInvoice = async (inv) => {
    if (!window.confirm(`Rechnung ${inv.invoice_number} wirklich stornieren?\n\nEs wird eine Stornorechnung erstellt (rechtlich korrekte Methode).`)) return;
    try {
      const res = await invoiceApi.cancel(inv.id);
      toast.success(`Storno-Rechnung ${res.data.storno_invoice?.invoice_number} erstellt`);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDeleteInvoice = async (inv) => {
    if (!window.confirm(`Rechnung ${inv.invoice_number} endgültig löschen?\n\nAchtung: Dies sollte nur bei Fehlbuchungen/Entwürfen gemacht werden. Für bezahlte oder ausgestellte Rechnungen bitte Storno verwenden.`)) return;
    try {
      await invoiceApi.delete(inv.id);
      toast.success('Rechnung gelöscht');
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleSendReminder = async (invId) => {
    try {
      const res = await invoiceApi.sendReminder(invId);
      toast.success(res.data.message);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadReminderPdf = async (invId, invNumber, level) => {
    try {
      const res = await invoiceApi.getReminderPdf(invId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `mahnung_${invNumber}_${level}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalPaid = invoices.reduce((s, i) => s + i.paid_amount, 0);
  const totalOpen = invoices.reduce((s, i) => s + (i.total - i.paid_amount), 0);
  const totalDeposits = deposits.filter(d => d.status === 'hinterlegt').reduce((s, d) => s + d.amount, 0);

  const selectedBooking = bookingsWithoutInvoice.find(b => b.id === selectedBookingId);

  return (
    <div className="p-6 space-y-6" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#111418]">Finanzen</h1>
          <p className="text-[#525866] mt-1">Rechnungen, Kautionen & Quittungen</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm p-1">
          <TabsTrigger value="invoices" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-invoices">
            Rechnungen
          </TabsTrigger>
          <TabsTrigger value="deposits" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-deposits">
            Kautionen
          </TabsTrigger>
          <TabsTrigger value="receipts" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-receipts">
            Quittungen
          </TabsTrigger>
          <TabsTrigger value="dunning" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-dunning">
            Mahnwesen {overdueInvoices.length > 0 && <span className="ml-1 bg-[#E60000] text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">{overdueInvoices.length}</span>}
          </TabsTrigger>
        </TabsList>

        {/* ============ RECHNUNGEN TAB ============ */}
        <TabsContent value="invoices" className="space-y-4 mt-4">
          <div className="flex flex-wrap gap-3">
            <Button onClick={openBookingInvoice} className="btn-primary" data-testid="create-booking-invoice-btn">
              <Plus weight="bold" className="mr-2" size={16} /> Buchungsrechnung
            </Button>
            <Button onClick={() => { setFreeCustomerId(''); setLineItems([{description:'',quantity:1,unit_price:0}]); setFreeDiscount(0); setFreeNotes(''); setShowFreeInvoice(true); }} className="btn-secondary" data-testid="create-free-invoice-btn">
              <Plus weight="bold" className="mr-2" size={16} /> Freie Rechnung
            </Button>
            <Button onClick={openStandaloneCreditModal} variant="outline" className="border-[#009951] text-[#009951] hover:bg-[#009951]/10" data-testid="create-credit-note-btn">
              <ReceiptX weight="bold" className="mr-2" size={16} /> Neue Gutschrift
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="stat-card"><p className="overline">Bezahlt</p><p className="stat-value text-[#009951]">€{totalPaid.toFixed(2)}</p></div>
            <div className="stat-card"><p className="overline">Ausstehend</p><p className="stat-value text-[#FFB800]">€{totalOpen.toFixed(2)}</p></div>
          </div>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48 input-swiss"><Funnel className="mr-2" size={16} /><SelectValue placeholder="Alle Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              {Object.entries(INV_STATUS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>

          {invoices.length === 0 ? (
            <div className="card-swiss p-12 text-center"><Receipt weight="thin" size={64} className="mx-auto text-[#8A94A6] mb-4" /><p className="text-[#525866]">Keine Rechnungen</p></div>
          ) : (
            <div className="card-swiss overflow-hidden">
              <table className="table-swiss">
                <thead><tr><th>Rechnung</th><th>Typ</th><th>Kunde</th><th>Betrag</th><th>Bezahlt</th><th>Offen</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id}>
                      <td><p className="font-semibold text-[#111418]">{inv.invoice_number}</p><p className="text-xs text-[#8A94A6]">{new Date(inv.created_at).toLocaleDateString('de-DE')}</p></td>
                      <td><span className={`badge ${
                        inv.invoice_type === 'free' ? 'badge-neutral' :
                        inv.invoice_type === 'storno' ? 'badge-danger' :
                        inv.invoice_type === 'gutschrift' ? 'badge-success' :
                        inv.invoice_type === 'damage' ? 'badge-warning' :
                        'badge-success'
                      }`}>
                        {inv.invoice_type === 'free' ? 'Frei' :
                         inv.invoice_type === 'storno' ? 'Storno' :
                         inv.invoice_type === 'gutschrift' ? 'Gutschrift' :
                         inv.invoice_type === 'damage' ? 'Schaden' : 'Buchung'}
                      </span></td>
                      <td>{inv.customer?.first_name} {inv.customer?.last_name}</td>
                      <td className={`font-medium ${(inv.total || 0) < 0 || inv.invoice_type === 'gutschrift' ? 'text-[#009951]' : ''}`}>
                        {inv.invoice_type === 'gutschrift' ? '−' : ''}€{Math.abs(inv.total || 0).toFixed(2)}
                      </td>
                      <td className="text-[#009951]">€{(inv.paid_amount || 0).toFixed(2)}</td>
                      <td className={(inv.total || 0) - (inv.paid_amount || 0) > 0 ? 'text-[#E60000] font-medium' : 'text-[#009951]'}>€{((inv.total || 0) - (inv.paid_amount || 0)).toFixed(2)}</td>
                      <td>{getInvBadge(inv.status)}</td>
                      <td><div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => setPreviewInvoice(inv)} aria-label={`Vorschau ${inv.invoice_number || "Rechnung"}`} data-testid={`preview-invoice-${inv.id}`}><Eye weight="bold" size={16} className="mr-1" />Vorschau</Button>
                        {inv.booking_id && <Link to={`/bookings/${inv.booking_id}`}><Button variant="ghost" size="sm" title="Öffnen"><Eye weight="bold" size={16} /></Button></Link>}
                        {inv.invoice_type === 'gutschrift' ? (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => downloadCreditNotePdf(inv)} title="Gutschrift-PDF" data-testid={`credit-pdf-${inv.id}`}>
                              <FileArrowDown weight="bold" size={16} className="text-[#009951]" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditCreditNote(inv)}
                              title="Gutschrift bearbeiten"
                              data-testid={`edit-credit-note-${inv.id}`}
                            >
                              <PencilSimple weight="bold" size={16} className="text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCreditNote(inv)}
                              title="Gutschrift löschen"
                              data-testid={`delete-credit-note-${inv.id}`}
                            >
                              <Trash weight="bold" size={16} className="text-red-600" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => downloadInvoicePdf(inv)} title="PDF"><FileArrowDown weight="bold" size={16} /></Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Rechnung per E-Mail senden (mit GiroCode)"
                              data-testid={`send-email-${inv.id}`}
                              onClick={async () => {
                                if (!window.confirm(`Rechnung ${inv.invoice_number} per E-Mail an den Kunden versenden?`)) return;
                                try {
                                  const r = await invoiceApi.sendEmail(inv.id);
                                  toast.success(
                                    r.data.email_sent ? `Rechnung an ${r.data.customer_email} versandt` : 'E-Mail-Versand nicht konfiguriert',
                                    { description: r.data.with_girocode ? 'GiroCode wurde inline eingebettet.' : undefined }
                                  );
                                } catch (e) { toast.error(formatApiError(e)); }
                              }}
                            >
                              <Envelope weight="bold" size={16} />
                            </Button>
                            {onlinePaymentsEnabled && inv.status !== 'paid' && (Number(inv.total) - Number(inv.paid_amount || 0)) > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleStripePayment(inv)}
                                title="Online bezahlen (Stripe)"
                                data-testid={`stripe-pay-${inv.id}`}
                              >
                                <CreditCard weight="bold" size={16} className="text-[#635BFF]" />
                              </Button>
                            )}
                          </>
                        )}
                        {inv.invoice_type !== 'storno' && inv.invoice_type !== 'gutschrift' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditInvoice(inv)}
                            title="Bearbeiten"
                            data-testid={`edit-invoice-${inv.id}`}
                          >
                            <PencilSimple weight="bold" size={16} className="text-blue-600" />
                          </Button>
                        )}
                        {inv.invoice_type !== 'storno' && inv.invoice_type !== 'gutschrift' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openCreditModalForInvoice(inv)}
                            title="Gutschrift erstellen"
                            data-testid={`credit-invoice-${inv.id}`}
                          >
                            <ReceiptX weight="bold" size={16} className="text-[#009951]" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setHistoryInvoice(inv)}
                          title="Verlauf"
                          data-testid={`history-invoice-${inv.id}`}
                        >
                          <ClockCounterClockwise weight="bold" size={16} className="text-[#525866]" />
                        </Button>
                        {inv.status !== 'cancelled' && inv.invoice_type !== 'storno' && inv.invoice_type !== 'gutschrift' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancelInvoice(inv)}
                            title="Stornieren"
                            data-testid={`cancel-invoice-${inv.id}`}
                          >
                            <X weight="bold" size={16} className="text-orange-600" />
                          </Button>
                        )}
                        {(inv.paid_amount || 0) === 0 && inv.invoice_type !== 'storno' && inv.invoice_type !== 'gutschrift' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteInvoice(inv)}
                            title="Löschen"
                            data-testid={`delete-invoice-${inv.id}`}
                          >
                            <Trash weight="bold" size={16} className="text-red-600" />
                          </Button>
                        )}
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ============ KAUTIONEN TAB ============ */}
        <TabsContent value="deposits" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <div className="stat-card inline-flex items-center gap-4 px-6">
              <ShieldCheck weight="fill" size={24} className="text-[#009951]" />
              <div><p className="overline">Hinterlegte Kautionen</p><p className="stat-value">€{totalDeposits.toFixed(2)}</p></div>
            </div>
            <Button onClick={() => { setDepCustomerId(''); setDepAmount(''); setDepDescription('Kaution'); setDepNotes(''); setShowDeposit(true); }} className="btn-primary" data-testid="create-deposit-btn">
              <Plus weight="bold" className="mr-2" size={16} /> Kaution hinterlegen
            </Button>
          </div>

          {deposits.length === 0 ? (
            <div className="card-swiss p-12 text-center"><ShieldCheck weight="thin" size={64} className="mx-auto text-[#8A94A6] mb-4" /><p className="text-[#525866]">Keine Kautionen</p></div>
          ) : (
            <div className="card-swiss overflow-hidden">
              <table className="table-swiss">
                <thead><tr><th>Kunde</th><th>Beschreibung</th><th>Betrag</th><th>Status</th><th>Datum</th><th></th></tr></thead>
                <tbody>
                  {deposits.map(dep => (
                    <tr key={dep.id} data-testid={`deposit-row-${dep.id}`}>
                      <td className="font-medium">{dep.customer?.first_name} {dep.customer?.last_name}</td>
                      <td>{dep.description}</td>
                      <td className="font-bold">€{dep.amount.toFixed(2)}</td>
                      <td><span className={`badge ${dep.status === 'hinterlegt' ? 'badge-success' : 'badge-neutral'}`}>{DEP_STATUS[dep.status]}</span></td>
                      <td className="text-sm text-[#8A94A6]">{new Date(dep.created_at).toLocaleDateString('de-DE')}</td>
                      <td>
                        {dep.status === 'hinterlegt' && (
                          <Button variant="outline" size="sm" onClick={() => handleReturnDeposit(dep)} data-testid={`return-deposit-${dep.id}`}>
                            <ArrowUDownLeft weight="bold" size={14} className="mr-1" /> Zurückgeben
                          </Button>
                        )}
                        {dep.returned_at && <p className="text-xs text-[#8A94A6]">Zurück: {new Date(dep.returned_at).toLocaleDateString('de-DE')}</p>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ============ QUITTUNGEN TAB ============ */}
        <TabsContent value="receipts" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => { setRcptCustomerId(''); setRcptAmount(''); setRcptDescription(''); setRcptMethod('bar'); setRcptSignature(null); setRcptNotes(''); setShowReceipt(true); }} className="btn-primary" data-testid="create-receipt-btn">
              <Plus weight="bold" className="mr-2" size={16} /> Quittung erstellen
            </Button>
          </div>

          {receipts.length === 0 ? (
            <div className="card-swiss p-12 text-center"><HandCoins weight="thin" size={64} className="mx-auto text-[#8A94A6] mb-4" /><p className="text-[#525866]">Keine Quittungen</p></div>
          ) : (
            <div className="card-swiss overflow-hidden">
              <table className="table-swiss">
                <thead><tr><th>Quittung</th><th>Kunde</th><th>Beschreibung</th><th>Betrag</th><th>Art</th><th>Unterschrift</th><th></th></tr></thead>
                <tbody>
                  {receipts.map(r => (
                    <tr key={r.id}>
                      <td><p className="font-semibold">{r.receipt_number}</p><p className="text-xs text-[#8A94A6]">{new Date(r.created_at).toLocaleDateString('de-DE')}</p></td>
                      <td>{r.customer?.first_name} {r.customer?.last_name}</td>
                      <td>{r.description}</td>
                      <td className="font-bold">€{r.amount.toFixed(2)}</td>
                      <td><span className="badge badge-neutral">{r.payment_method === 'bar' ? 'Bar' : r.payment_method === 'ec' ? 'EC' : 'Überw.'}</span></td>
                      <td>{r.signature_data ? <CheckCircle weight="fill" size={18} className="text-[#009951]" /> : <span className="text-xs text-[#8A94A6]">Keine</span>}</td>
                      <td><Button variant="ghost" size="sm" onClick={() => downloadReceiptPdf(r.id, r.receipt_number)} data-testid={`download-receipt-${r.id}`}><FileArrowDown weight="bold" size={16} /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ============ MAHNWESEN TAB ============ */}
        <TabsContent value="dunning" className="space-y-4 mt-4">
          {overdueInvoices.length === 0 ? (
            <div className="card-swiss p-12 text-center">
              <CheckCircle weight="thin" size={64} className="mx-auto text-[#009951] mb-4" />
              <p className="text-lg font-bold text-[#009951]">Keine überfälligen Rechnungen</p>
              <p className="text-sm text-[#8A94A6] mt-1">Alle Rechnungen sind im grünen Bereich</p>
            </div>
          ) : (
            <>
              <div className="stat-card border-l-4 border-l-[#E60000]">
                <div className="flex items-center gap-3">
                  <Bell weight="fill" size={24} className="text-[#E60000]" />
                  <div>
                    <p className="font-bold text-[#E60000]">{overdueInvoices.length} überfällige Rechnungen</p>
                    <p className="text-sm text-[#525866]">Offener Gesamtbetrag: <b>€{overdueInvoices.reduce((s, i) => s + i.open_amount, 0).toFixed(2)}</b></p>
                  </div>
                </div>
              </div>

              <div className="card-swiss overflow-hidden">
                <table className="table-swiss">
                  <thead><tr><th>Rechnung</th><th>Kunde</th><th>Offen</th><th>Fällig seit</th><th>Tage überfällig</th><th>Mahnstufe</th><th></th></tr></thead>
                  <tbody>
                    {overdueInvoices.map(inv => (
                      <tr key={inv.id} data-testid={`overdue-${inv.id}`}>
                        <td><p className="font-semibold">{inv.invoice_number}</p></td>
                        <td>{inv.customer?.first_name} {inv.customer?.last_name}</td>
                        <td className="font-bold text-[#E60000]">€{inv.open_amount.toFixed(2)}</td>
                        <td className="text-sm">{inv.due_date ? new Date(inv.due_date).toLocaleDateString('de-DE') : '—'}</td>
                        <td>
                          <span className={`badge ${inv.days_overdue > 30 ? 'badge-error' : inv.days_overdue > 14 ? 'badge-warning' : 'badge-neutral'}`}>
                            {inv.days_overdue} Tage
                          </span>
                        </td>
                        <td><span className="text-sm font-medium">{REMINDER_LABELS[inv.reminder_level || 0]}</span></td>
                        <td>
                          <div className="flex gap-1">
                            <Button variant="outline" size="sm" onClick={() => handleSendReminder(inv.id)} className="text-[#E60000] border-[#E60000] hover:bg-red-50" data-testid={`send-reminder-${inv.id}`}>
                              <Bell weight="bold" size={14} className="mr-1" />
                              {(inv.reminder_level || 0) === 0 ? 'Erinnern' : 'Mahnen'}
                            </Button>
                            {(inv.reminder_level || 0) > 0 && (
                              <Button variant="ghost" size="sm" onClick={() => downloadReminderPdf(inv.id, inv.invoice_number, inv.reminder_level)} title="Mahnung PDF">
                                <FileArrowDown weight="bold" size={16} />
                              </Button>
                            )}
                            {inv.booking_id && (
                              <Link to={`/bookings/${inv.booking_id}`}><Button variant="ghost" size="sm"><Eye weight="bold" size={16} /></Button></Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* ======= BOOKING INVOICE MODAL ======= */}
      <Dialog open={showBookingInvoice} onOpenChange={setShowBookingInvoice}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Buchungsrechnung erstellen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {bookingsWithoutInvoice.length === 0 ? (
              <p className="text-center text-[#8A94A6] py-4">Alle Buchungen haben bereits eine Rechnung</p>
            ) : (
              <div>
                <Label className="overline">Buchung</Label>
                <Select value={selectedBookingId} onValueChange={setSelectedBookingId}>
                  <SelectTrigger className="input-swiss"><SelectValue placeholder="Buchung wählen..." /></SelectTrigger>
                  <SelectContent>{bookingsWithoutInvoice.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.customer?.first_name} {b.customer?.last_name} — {b.vehicle?.brand} {b.vehicle?.model} — €{b.price_info?.total_price?.toFixed(2)}</SelectItem>
                  ))}</SelectContent>
                </Select>
              </div>
            )}
            {selectedBooking && (
              <div className="p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm text-sm space-y-1">
                <div className="flex justify-between"><span className="text-[#8A94A6]">Fahrzeug</span><span>{selectedBooking.vehicle?.brand} {selectedBooking.vehicle?.model}</span></div>
                <div className="flex justify-between"><span className="text-[#8A94A6]">Zeitraum</span><span>{selectedBooking.price_info?.days} Tage</span></div>
                <div className="flex justify-between font-bold border-t border-[#E2E4E9] pt-1"><span>Preis</span><span className="text-[#E60000]">€{selectedBooking.price_info?.total_price?.toFixed(2)}</span></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="overline">Zusatzgebühren (€)</Label><Input type="number" value={additionalCharges} onChange={e => setAdditionalCharges(parseFloat(e.target.value)||0)} className="input-swiss" min={0} step={0.01} /></div>
              <div><Label className="overline">Rabatt (€)</Label><Input type="number" value={invoiceDiscount} onChange={e => setInvoiceDiscount(parseFloat(e.target.value)||0)} className="input-swiss" min={0} step={0.01} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingInvoice(false)}>Abbrechen</Button>
            <Button className="btn-primary" onClick={submitBookingInvoice} disabled={!selectedBookingId}>Erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ======= FREE INVOICE MODAL ======= */}
      <Dialog open={showFreeInvoice} onOpenChange={setShowFreeInvoice}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Freie Rechnung erstellen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="overline">Kunde</Label>
                <button type="button" onClick={() => setShowQuickCustomer(true)} className="text-xs text-[#E60000] hover:underline flex items-center gap-1"><Plus weight="bold" size={12} /> Neuer Kunde</button>
              </div>
              <Select value={freeCustomerId} onValueChange={setFreeCustomerId}>
                <SelectTrigger className="input-swiss" data-testid="free-invoice-customer"><SelectValue placeholder="Kunde wählen..." /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.email ? `— ${c.email}` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="overline">Positionen</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} data-testid="add-line-item-btn">
                  <Plus weight="bold" size={14} className="mr-1" /> Position
                </Button>
              </div>
              {lineItems.map((item, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end">
                  <div className="col-span-5">
                    {i === 0 && <Label className="text-xs text-[#8A94A6]">Beschreibung</Label>}
                    <Input value={item.description} onChange={e => updateLineItem(i, 'description', e.target.value)} className="input-swiss" placeholder="z.B. Kaution, Reinigung..." data-testid={`line-item-desc-${i}`} />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <Label className="text-xs text-[#8A94A6]">Menge</Label>}
                    <Input type="number" value={item.quantity} onChange={e => updateLineItem(i, 'quantity', parseInt(e.target.value)||1)} className="input-swiss" min={1} />
                  </div>
                  <div className="col-span-3">
                    {i === 0 && <Label className="text-xs text-[#8A94A6]">Einzelpreis (€)</Label>}
                    <Input type="number" value={item.unit_price} onChange={e => updateLineItem(i, 'unit_price', parseFloat(e.target.value)||0)} className="input-swiss" min={0} step={0.01} data-testid={`line-item-price-${i}`} />
                  </div>
                  <div className="col-span-2 flex items-center gap-1">
                    <span className="text-sm font-medium">€{(item.quantity * item.unit_price).toFixed(2)}</span>
                    {lineItems.length > 1 && <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem(i)} className="text-[#E60000] p-1"><Trash weight="bold" size={14} /></Button>}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div><Label className="overline">Rabatt (€)</Label><Input type="number" value={freeDiscount} onChange={e => setFreeDiscount(parseFloat(e.target.value)||0)} className="input-swiss" min={0} step={0.01} /></div>
              <div className="flex items-end"><div className="p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm w-full text-right"><p className="text-xs text-[#8A94A6]">Netto-Gesamt</p><p className="text-xl font-bold text-[#E60000]">€{freeTotal.toFixed(2)}</p></div></div>
            </div>

            <div><Label className="overline">Notizen</Label><textarea value={freeNotes} onChange={e => setFreeNotes(e.target.value)} className="input-swiss w-full px-3 py-2" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFreeInvoice(false)}>Abbrechen</Button>
            <Button className="btn-primary" onClick={submitFreeInvoice} disabled={!freeCustomerId || lineItems.every(i => !i.description)} data-testid="submit-free-invoice-btn">Rechnung erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ======= EDIT INVOICE MODAL ======= */}
      <Dialog open={showEditInvoice} onOpenChange={setShowEditInvoice}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Rechnung bearbeiten {editingInvoice?.invoice_number ? `· ${editingInvoice.invoice_number}` : ''}</DialogTitle>
          </DialogHeader>
          {editingInvoice && (
            <div className="space-y-4">
              {editingInvoice.paid_amount > 0 && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-sm text-sm text-orange-800">
                  <strong>Hinweis:</strong> Diese Rechnung wurde bereits (teil-)bezahlt (€ {Number(editingInvoice.paid_amount).toFixed(2)}). Änderungen können GoBD-Implikationen haben. Für rechtssichere Korrekturen bitte stornieren.
                </div>
              )}
              <div>
                <Label className="overline">Kunde (aus Stammdaten wählen)</Label>
                <Select value={editInvCustomerId} onValueChange={setEditInvCustomerId}>
                  <SelectTrigger className="input-swiss"><SelectValue /></SelectTrigger>
                  <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.email ? `— ${c.email}` : ''}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <CustomerOverrideFields
                customer={customers.find((c) => c.id === editInvCustomerId) || editingInvoice?.customer}
                override={editInvCustomerOverride}
                onChange={setEditInvCustomerOverride}
              />

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="overline">Positionen</Label>
                  <Button type="button" variant="outline" size="sm" onClick={editInvAddItem}>
                    <Plus weight="bold" size={14} className="mr-1" /> Position
                  </Button>
                </div>
                {editInvItems.map((item, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end">
                    <div className="col-span-5">
                      {i === 0 && <Label className="text-xs text-[#8A94A6]">Beschreibung</Label>}
                      <Input value={item.description} onChange={e => editInvUpdateItem(i, 'description', e.target.value)} className="input-swiss" data-testid={`edit-inv-desc-${i}`} />
                    </div>
                    <div className="col-span-2">
                      {i === 0 && <Label className="text-xs text-[#8A94A6]">Menge</Label>}
                      <Input type="number" value={item.quantity} onChange={e => editInvUpdateItem(i, 'quantity', e.target.value)} className="input-swiss" min={1} />
                    </div>
                    <div className="col-span-3">
                      {i === 0 && <Label className="text-xs text-[#8A94A6]">Einzelpreis (€)</Label>}
                      <Input type="number" value={item.unit_price} onChange={e => editInvUpdateItem(i, 'unit_price', e.target.value)} className="input-swiss" step={0.01} />
                    </div>
                    <div className="col-span-2 flex items-center gap-1">
                      <span className="text-sm font-medium">€{(item.quantity * item.unit_price).toFixed(2)}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => editInvRemoveItem(i)}
                        className="text-[#E60000] p-1"
                        title="Position löschen"
                        data-testid={`remove-line-item-${i}`}
                      >
                        <Trash weight="bold" size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="overline">Zusatz (€)</Label>
                  <Input type="number" value={editInvAdditional} onChange={e => setEditInvAdditional(e.target.value)} className="input-swiss" step={0.01} />
                </div>
                <div>
                  <Label className="overline">Rabatt (€)</Label>
                  <Input type="number" value={editInvDiscount} onChange={e => setEditInvDiscount(e.target.value)} className="input-swiss" step={0.01} />
                </div>
                <div>
                  <Label className="overline">MwSt. (%)</Label>
                  <Input type="number" value={editInvTaxRate} onChange={e => setEditInvTaxRate(e.target.value)} className="input-swiss" step={0.1} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="overline">Fälligkeitsdatum</Label>
                  <Input type="date" value={editInvDueDate} onChange={e => setEditInvDueDate(e.target.value)} className="input-swiss" />
                </div>
                <div className="flex items-end">
                  <div className="p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm w-full text-right">
                    <p className="text-xs text-[#8A94A6]">Gesamt (Brutto)</p>
                    <p className="text-xl font-bold text-[#E60000]">€{editInvTotal.toFixed(2)}</p>
                  </div>
                </div>
              </div>
              <div>
                <Label className="overline">Notizen</Label>
                <textarea value={editInvNotes} onChange={e => setEditInvNotes(e.target.value)} className="input-swiss w-full px-3 py-2" rows={2} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditInvoice(false)}>Abbrechen</Button>
            <Button className="btn-primary" onClick={submitEditInvoice} data-testid="submit-edit-invoice-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ======= DEPOSIT MODAL ======= */}
      <Dialog open={showDeposit} onOpenChange={setShowDeposit}>
        <DialogContent>
          <DialogHeader><DialogTitle>Kaution hinterlegen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="overline">Kunde</Label>
                <button type="button" onClick={() => setShowQuickCustomer(true)} className="text-xs text-[#E60000] hover:underline flex items-center gap-1"><Plus weight="bold" size={12} /> Neuer Kunde</button>
              </div>
              <Select value={depCustomerId} onValueChange={setDepCustomerId}>
                <SelectTrigger className="input-swiss" data-testid="deposit-customer"><SelectValue placeholder="Kunde wählen..." /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="overline">Betrag (€)</Label><Input type="number" value={depAmount} onChange={e => setDepAmount(e.target.value)} className="input-swiss" min={0} step={0.01} required data-testid="deposit-amount" /></div>
            <div>
              <Label className="overline">Zahlungsart</Label>
              <Select value={depPaymentMethod} onValueChange={setDepPaymentMethod}>
                <SelectTrigger className="input-swiss" data-testid="deposit-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Bar (ins Kassenbuch)</SelectItem>
                  <SelectItem value="ec">EC / Karte</SelectItem>
                  <SelectItem value="ueberweisung">Überweisung</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="overline">Beschreibung</Label><Input value={depDescription} onChange={e => setDepDescription(e.target.value)} className="input-swiss" data-testid="deposit-description" /></div>
            <div><Label className="overline">Notizen</Label><textarea value={depNotes} onChange={e => setDepNotes(e.target.value)} className="input-swiss w-full px-3 py-2" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeposit(false)}>Abbrechen</Button>
            <Button className="btn-primary" onClick={submitDeposit} disabled={!depCustomerId || !depAmount} data-testid="submit-deposit-btn">Kaution hinterlegen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ======= RECEIPT MODAL ======= */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Quittung erstellen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="overline">Kunde</Label>
                <button type="button" onClick={() => setShowQuickCustomer(true)} className="text-xs text-[#E60000] hover:underline flex items-center gap-1"><Plus weight="bold" size={12} /> Neuer Kunde</button>
              </div>
              <Select value={rcptCustomerId} onValueChange={setRcptCustomerId}>
                <SelectTrigger className="input-swiss" data-testid="receipt-customer"><SelectValue placeholder="Kunde wählen..." /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="overline">Betrag (€)</Label><Input type="number" value={rcptAmount} onChange={e => setRcptAmount(e.target.value)} className="input-swiss" min={0} step={0.01} required data-testid="receipt-amount" /></div>
            <div><Label className="overline">Beschreibung</Label><Input value={rcptDescription} onChange={e => setRcptDescription(e.target.value)} className="input-swiss" placeholder="z.B. Mietgebühr, Kaution..." required data-testid="receipt-description" /></div>
            <div>
              <Label className="overline">Zahlungsart</Label>
              <Select value={rcptMethod} onValueChange={setRcptMethod}>
                <SelectTrigger className="input-swiss"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Barzahlung</SelectItem>
                  <SelectItem value="ec">EC-Karte</SelectItem>
                  <SelectItem value="überweisung">Überweisung</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline mb-2 block">Unterschrift des Kunden</Label>
              <SignaturePad onSignatureChange={setRcptSignature} />
            </div>
            <div><Label className="overline">Notizen</Label><textarea value={rcptNotes} onChange={e => setRcptNotes(e.target.value)} className="input-swiss w-full px-3 py-2" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceipt(false)}>Abbrechen</Button>
            <Button className="btn-primary" onClick={submitReceipt} disabled={!rcptCustomerId || !rcptAmount || !rcptDescription} data-testid="submit-receipt-btn">Quittung erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ======= RETURN DEPOSIT MODAL (with receipt + signature) ======= */}
      <Dialog open={showReturnDeposit} onOpenChange={setShowReturnDeposit}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Kaution zurückgeben</DialogTitle></DialogHeader>
          {returningDeposit && (
            <div className="space-y-4">
              <div className="p-4 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#8A94A6]">Kunde</span>
                  <span className="font-medium">{returningDeposit.customer?.first_name} {returningDeposit.customer?.last_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#8A94A6]">Beschreibung</span>
                  <span className="font-medium">{returningDeposit.description}</span>
                </div>
                <div className="flex justify-between text-sm font-bold pt-2 border-t border-[#E2E4E9]">
                  <span>Rückgabebetrag</span>
                  <span className="text-[#009951] text-lg">€{returningDeposit.amount.toFixed(2)}</span>
                </div>
              </div>

              <div>
                <Label className="overline">Auszahlungsart</Label>
                <Select value={returnMethod} onValueChange={setReturnMethod}>
                  <SelectTrigger className="input-swiss"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bar">Barzahlung</SelectItem>
                    <SelectItem value="ec">EC-Karte</SelectItem>
                    <SelectItem value="überweisung">Überweisung</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="overline mb-2 block">Unterschrift des Kunden (Empfangsbestätigung)</Label>
                <SignaturePad onSignatureChange={setReturnSignature} />
              </div>

              <p className="text-xs text-[#8A94A6]">
                Es wird automatisch eine Quittung über die Kautionsrückgabe erstellt.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReturnDeposit(false)}>Abbrechen</Button>
            <Button className="btn-primary" onClick={confirmReturnDeposit} data-testid="confirm-return-deposit-btn">
              Kaution zurückgeben & Quittung erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuickCustomerCreate
        open={showQuickCustomer}
        onOpenChange={setShowQuickCustomer}
        onCreated={(newCustomer) => {
          setCustomers(prev => [...prev, newCustomer]);
          // Auto-select in the currently active modal
          setFreeCustomerId(newCustomer.id);
          setDepCustomerId(newCustomer.id);
          setRcptCustomerId(newCustomer.id);
        }}
      />

      <RevisionHistory
        open={!!historyInvoice}
        onOpenChange={(v) => !v && setHistoryInvoice(null)}
        entityType="invoice"
        entityId={historyInvoice?.id}
        title={historyInvoice?.invoice_number}
      />

      {/* Gutschrift / Credit Note Modal */}
      <Dialog open={showCreditModal} onOpenChange={(v) => { setShowCreditModal(v); if (!v) setEditingCreditNote(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCreditNote
                ? `Gutschrift ${editingCreditNote.invoice_number} bearbeiten`
                : (creditInvoice ? `Gutschrift zu Rechnung ${creditInvoice.invoice_number}` : 'Neue eigenständige Gutschrift')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="overline">Gutschrift-Typ</Label>
              <Select value={creditType} onValueChange={setCreditType}>
                <SelectTrigger className="input-swiss" data-testid="credit-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Gutschrift (Standard, ohne Pflichthinweis)</SelectItem>
                  <SelectItem value="gutschriftverfahren">Abrechnung nach Gutschriftverfahren (§14 Abs. 2 Satz 2 UStG)</SelectItem>
                  <SelectItem value="storno">Stornogutschrift</SelectItem>
                  <SelectItem value="kulanz">Kulanzgutschrift</SelectItem>
                  <SelectItem value="custom">Eigener Hinweis…</SelectItem>
                </SelectContent>
              </Select>
              {creditType === 'gutschriftverfahren' && (
                <p className="text-xs text-[#009951] mt-1">
                  Der Pflichthinweis „Abrechnung nach Gutschriftverfahren gemäß §14 Abs. 2 Satz 2 UStG" wird auf das PDF gedruckt.
                </p>
              )}
              {creditType === 'custom' && (
                <Input
                  value={creditCustomLegal}
                  onChange={(e) => setCreditCustomLegal(e.target.value)}
                  className="input-swiss mt-2"
                  placeholder="Eigener rechtlicher Hinweis für das PDF"
                />
              )}
            </div>

            {!creditInvoice && (
              <div>
                <Label className="overline">Kunde *</Label>
                <Select value={creditCustomerId} onValueChange={setCreditCustomerId}>
                  <SelectTrigger className="input-swiss" data-testid="credit-customer-select"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                  <SelectContent>
                    {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.email ? `— ${c.email}` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {editingCreditNote && (
              <CustomerOverrideFields
                customer={customers.find((c) => c.id === creditCustomerId)}
                override={creditCustomerOverride}
                onChange={setCreditCustomerOverride}
                title="Kundendaten (nur für diese Gutschrift)"
              />
            )}

            {creditInvoice && !editingCreditNote && (
              <div className="p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm text-sm">
                <div className="flex justify-between"><span className="text-[#8A94A6]">Original-Rechnung</span><span className="font-medium">{creditInvoice.invoice_number}</span></div>
                <div className="flex justify-between mt-1"><span className="text-[#8A94A6]">Rechnungssumme</span><span className="font-medium">€ {Number(creditInvoice.total || 0).toFixed(2)}</span></div>
                <div className="flex justify-between mt-1"><span className="text-[#8A94A6]">bereits bezahlt</span><span className="font-medium">€ {Number(creditInvoice.paid_amount || 0).toFixed(2)}</span></div>
              </div>
            )}

            <div>
              <Label className="overline">Gutschriftsbetrag (Brutto, €) *</Label>
              <Input
                type="number" step="0.01" min={0.01}
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                className="input-swiss"
                placeholder="z.B. 50.00"
                data-testid="credit-amount"
              />
              {creditInvoice && (
                <p className="text-xs text-[#8A94A6] mt-1">Maximal: € {Number(creditInvoice.total || 0).toFixed(2)}</p>
              )}
            </div>

            {!creditInvoice && (
              <div>
                <Label className="overline">Kurzbeschreibung</Label>
                <Input
                  value={creditDescription}
                  onChange={(e) => setCreditDescription(e.target.value)}
                  className="input-swiss"
                  placeholder="z.B. Kulanzgutschrift Wartezeit"
                />
              </div>
            )}

            <div>
              <Label className="overline">Grund *</Label>
              <Input
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                className="input-swiss"
                placeholder="z.B. Reklamation Heckleuchte / Kulanz"
                data-testid="credit-reason"
                required
              />
            </div>

            <div>
              <Label className="overline">Zusätzliche Notizen</Label>
              <textarea
                value={creditNotes}
                onChange={(e) => setCreditNotes(e.target.value)}
                className="input-swiss w-full px-3 py-2"
                rows={2}
              />
            </div>

            {creditInvoice && (
              <label className="flex items-start gap-2 text-sm p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
                <input
                  type="checkbox"
                  checked={creditApplyToPaid}
                  onChange={(e) => setCreditApplyToPaid(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  <strong>Erstattung:</strong> Gutschrift-Betrag auch vom „bezahlt"-Feld der Original-Rechnung abziehen
                  (Geld wird tatsächlich an Kunden zurückgezahlt).
                </span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreditModal(false); setEditingCreditNote(null); }}>Abbrechen</Button>
            <Button
              onClick={submitCreditNote}
              className="bg-[#009951] text-white hover:bg-[#007a41]"
              disabled={!creditReason || !creditAmount || (!creditInvoice && !creditCustomerId)}
              data-testid="submit-credit-note-btn"
            >
              {editingCreditNote ? 'Änderungen speichern' : 'Gutschrift erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {previewInvoice && <InvoicePreview key={previewInvoice.id} invoice={previewInvoice} onClose={() => setPreviewInvoice(null)} />}
    </div>
  );
};

export default Invoices;
